import { useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { X, RefreshCw } from 'lucide-react';
import { usePWAUpdate } from '@/hooks/use-pwa-update';

const BANNER_HEIGHT = '80px';

export function UpdateBanner() {
  const { hasUpdate, update, dismiss } = usePWAUpdate();

  useEffect(() => {
    if (hasUpdate) {
      document.body.style.paddingBottom = BANNER_HEIGHT;
      return () => {
        document.body.style.paddingBottom = '';
      };
    }
  }, [hasUpdate]);

  if (!hasUpdate) return null;

  return (
    <aside
      role="complementary"
      aria-label="Application update available"
      className="fixed bottom-0 left-0 right-0 z-50 p-3 bg-primary text-primary-foreground shadow-lg border-t animate-in slide-in-from-bottom-5"
    >
      <div className="container mx-auto">
        {/* Mobile: stacked layout */}
        <div className="flex flex-col gap-2 sm:hidden">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
              <p className="font-medium text-sm">Update available</p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={dismiss}
              aria-label="Dismiss update banner"
              className="h-7 w-7 text-primary-foreground/80 hover:text-primary-foreground hover:bg-primary-foreground/10"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={update}
            className="w-full gap-1"
          >
            <RefreshCw className="h-4 w-4" aria-hidden="true" />
            Update Now
          </Button>
        </div>

        {/* Desktop: horizontal layout */}
        <div className="hidden sm:flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-foreground/10 rounded-full">
              <RefreshCw className="h-5 w-5" aria-hidden="true" />
            </div>
            <div>
              <p className="font-medium text-sm">New version available</p>
              <p className="text-xs text-primary-foreground/80">
                Tap update to get the latest features
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={update}
              className="gap-1"
            >
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
              Update
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={dismiss}
              aria-label="Dismiss update banner"
              className="h-8 w-8 text-primary-foreground/80 hover:text-primary-foreground hover:bg-primary-foreground/10"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
        </div>
      </div>
    </aside>
  );
}
